import React, { useState, useEffect, useCallback } from 'react';
import {
  Link2, CheckCircle2, AlertTriangle, XCircle, RefreshCw, Loader2, ExternalLink,
} from 'lucide-react';
import { apiCall, buildUrl, default as API_CONFIG } from '../config/api';

const PORTFOLIO_LABELS = {
  'schwab-joint': 'Joint',
  'schwab-individual': 'Individual',
  'schwab-ira': 'IRA',
};

const STATUS_STYLES = {
  healthy:      { Icon: CheckCircle2,  color: 'var(--success-color)', label: 'Connected' },
  expiring:     { Icon: AlertTriangle, color: 'var(--warning-color)', label: 'Expiring soon' },
  expired:      { Icon: XCircle,       color: 'var(--danger-color)',  label: 'Reconnection required' },
  disconnected: { Icon: XCircle,       color: 'var(--danger-color)',  label: 'Not connected' },
};

/** "3 hours ago" / "in 2 days" — coarse on purpose; exact timestamps go in the title attribute. */
export const relative = (iso) => {
  if (!iso) return null;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return null;

  const seconds = Math.round((Date.now() - then) / 1000);
  const ago = seconds >= 0;
  const abs = Math.abs(seconds);

  const [value, unit] =
    abs < 60      ? [abs, 'second'] :
    abs < 3600    ? [Math.floor(abs / 60), 'minute'] :
    abs < 86400   ? [Math.floor(abs / 3600), 'hour'] :
                    [Math.floor(abs / 86400), 'day'];

  const plural = `${value} ${unit}${value === 1 ? '' : 's'}`;
  return ago ? `${plural} ago` : `in ${plural}`;
};

export const absolute = (iso) => (iso ? new Date(iso).toLocaleString() : '—');

const money = (n) =>
  typeof n === 'number'
    ? n.toLocaleString(undefined, { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : '—';

/**
 * Schwab connection health.
 *
 * The integration's failure mode is silence: an expired authorization leaves
 * the last-known portfolio in place, so the app looks fine while the numbers
 * quietly go stale. This panel puts the two facts that reveal that side by
 * side — how much life the authorization has left, and when data last actually
 * moved — plus a way to force a sync and confirm.
 */
const SchwabConnection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    try {
      setStatus(await apiCall(API_CONFIG.endpoints.schwabStatus));
      setError(null);
    } catch (err) {
      setError('Could not load Schwab connection status.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const result = await apiCall(API_CONFIG.endpoints.schwabSync, { method: 'POST' });
      const accounts = result?.accountsSynced;
      setSyncResult({
        ok: true,
        message: accounts != null
          ? `Synced ${accounts} account${accounts === 1 ? '' : 's'}.`
          : 'Sync complete.',
      });
    } catch (err) {
      // The endpoint returns 500 with a reason when the authorization has
      // lapsed; apiCall surfaces that as a thrown error.
      setSyncResult({
        ok: false,
        message: 'Sync failed. If the authorization has expired, reconnect below and try again.',
      });
    } finally {
      setSyncing(false);
      // Refresh regardless: a failed sync still updates what we know.
      load({ quiet: true });
    }
  };

  if (loading) {
    return (
      <div className="card settings-card">
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Link2 size={20} />
            Schwab Connection
          </h2>
        </div>
        <div className="settings-form">
          <p className="settings-hint">Checking connection…</p>
        </div>
      </div>
    );
  }

  const style = STATUS_STYLES[status?.status] || STATUS_STYLES.disconnected;
  const { Icon } = style;
  const needsReconnect = status?.status === 'expired' || status?.status === 'disconnected';

  return (
    <div className="card settings-card">
      <div className="card-header">
        <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <Link2 size={20} />
          Schwab Connection
        </h2>
      </div>

      <div className="settings-form">
        {error && (
          <div className="settings-field">
            <p style={{ color: 'var(--danger-color)', margin: 0 }}>{error}</p>
          </div>
        )}

        {!error && status && (
          <>
            <div className="schwab-status-line" style={{ color: style.color }}>
              <Icon size={18} />
              <strong>{style.label}</strong>
            </div>
            <p className="settings-hint" style={{ marginTop: '0.25rem' }}>
              {status.statusDetail}
            </p>

            <div className="schwab-status-grid">
              <div>
                <span className="schwab-status-label">Authorized</span>
                <span className="schwab-status-value" title={absolute(status.authorizedAt)}>
                  {relative(status.authorizedAt) || '—'}
                </span>
              </div>
              <div>
                <span className="schwab-status-label">Token last refreshed</span>
                <span className="schwab-status-value" title={absolute(status.lastRefreshedAt)}>
                  {relative(status.lastRefreshedAt) || '—'}
                </span>
              </div>
              <div>
                <span className="schwab-status-label">Last portfolio sync</span>
                <span className="schwab-status-value" title={absolute(status.lastSyncAt)}>
                  {relative(status.lastSyncAt) || 'never'}
                </span>
              </div>
              <div>
                <span className="schwab-status-label">Reconnect needed</span>
                <span className="schwab-status-value">
                  {status.hoursUntilReauthNeeded != null
                    ? (status.hoursUntilReauthNeeded >= 24
                        ? `in ${Math.floor(status.hoursUntilReauthNeeded / 24)} days`
                        : `in ${Math.round(status.hoursUntilReauthNeeded)} hours`)
                    : 'now'}
                </span>
              </div>
            </div>

            {status.lastFailureAlertSentAt && (
              // Only appears when a refresh actually failed — a standing
              // "sync is down" signal that outlives the alert email.
              <p className="settings-hint" style={{ color: 'var(--danger-color)' }}>
                A token refresh failed {relative(status.lastFailureAlertSentAt)}. If portfolio
                values look stale, reconnect.
              </p>
            )}

            {status.portfolios?.length > 0 && (
              <div className="schwab-accounts">
                {status.portfolios.map((p) => (
                  <div key={p.portfolioType} className="schwab-account-row">
                    <span className="schwab-account-name">
                      {PORTFOLIO_LABELS[p.portfolioType] || p.portfolioType}
                    </span>
                    <span className="schwab-account-detail">
                      {p.holdingCount} holdings · {money(p.totalValue)}
                    </span>
                    <span className="schwab-account-detail" title={absolute(p.snapshotAt)}>
                      {relative(p.snapshotAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {syncResult && (
              <p
                className="settings-hint"
                style={{ color: syncResult.ok ? 'var(--success-color)' : 'var(--danger-color)' }}
              >
                {syncResult.message}
              </p>
            )}

            <div className="schwab-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleSync}
                disabled={syncing}
              >
                {syncing ? <Loader2 size={16} className="spin" /> : <RefreshCw size={16} />}
                {syncing ? 'Syncing…' : 'Sync now'}
              </button>

              <a
                className={`btn ${needsReconnect ? 'btn-primary' : 'btn-secondary'}`}
                href={buildUrl(API_CONFIG.endpoints.schwabAuth)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <ExternalLink size={16} />
                {needsReconnect ? 'Reconnect Schwab' : 'Reconnect'}
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SchwabConnection;
